// Porta de services/geolocation do legado para o header X-UY3-Client-Geo. Fica DESLIGADO por padrão
// (configureHttp({ clientGeo: true }) liga) até o Compliance decidir a base legal: geolocalização do
// operador é dado pessoal (LGPD, Lei 13.709/2018, art. 7º e art. 9º). Decisão final do Compliance/Jurídico UY3.
const MOCK_STORAGE_KEY = 'mock-geo-latlong';
const CACHE_TTL_IN_MS = 5 * 60 * 1000;
const FAILURE_CACHE_TTL_IN_MS = 60 * 1000;
const MAX_CONSECUTIVE_FAILURES = 3;

// eslint-disable-next-line lingui/no-unlocalized-strings -- nome de cabeçalho HTTP
export const GEO_HEADER = 'X-UY3-Client-Geo';

let cache: { value: string | null; expiresAt: number } | null = null;
let pendingRequest: Promise<string | null> | null = null;
let consecutiveFailures = 0;
let sessionDisabled = false;

const LAT_LONG_PATTERN = /^-?\d{1,3}(\.\d+)?,\s*-?\d{1,3}(\.\d+)?$/;

/** `<lat>,<long>` com 6 casas e ponto; `null` fora do formato ou do intervalo. */
export function parseLatLong(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed || !LAT_LONG_PATTERN.test(trimmed)) return null;
  const [latitude = NaN, longitude = NaN] = trimmed.split(',').map((part) => Number(part.trim()));
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null;
  return `${latitude.toFixed(6)},${longitude.toFixed(6)}`;
}

function getMockLatLong(): string | null {
  if (import.meta.env.PROD) return null;
  try {
    return parseLatLong(globalThis.localStorage.getItem(MOCK_STORAGE_KEY));
  } catch {
    return null;
  }
}

function recordResult(latLong: string | null) {
  if (latLong) {
    consecutiveFailures = 0;
    cache = { value: latLong, expiresAt: Date.now() + CACHE_TTL_IN_MS };
    return;
  }
  consecutiveFailures += 1;
  if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) sessionDisabled = true;
  cache = { value: null, expiresAt: Date.now() + FAILURE_CACHE_TTL_IN_MS };
}

function requestCurrentPosition(): Promise<string | null> {
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => resolve(`${position.coords.latitude.toFixed(6)},${position.coords.longitude.toFixed(6)}`),
      (error) => {
        if (error.code === error.PERMISSION_DENIED) sessionDisabled = true;
        resolve(null);
      },
      { enableHighAccuracy: false, timeout: 5000, maximumAge: CACHE_TTL_IN_MS },
    );
  });
}

/** Só o que já está em cache; nunca espera o GPS no caminho da request. */
export function getCachedGeoLatLong(): string | null {
  const mock = getMockLatLong();
  if (mock) return mock;
  if (cache && cache.expiresAt > Date.now()) return cache.value;
  return null;
}

/** Aquece o cache fora da request; chamadas concorrentes dividem a mesma Promise e nunca rejeitam. */
export function primeGeoLatLong(): Promise<string | null> {
  if (sessionDisabled) return Promise.resolve(null);
  const mock = getMockLatLong();
  if (mock) return Promise.resolve(mock);
  if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
    sessionDisabled = true;
    return Promise.resolve(null);
  }
  if (cache && cache.expiresAt > Date.now()) return Promise.resolve(cache.value);
  pendingRequest ??= requestCurrentPosition()
    .then((latLong) => {
      recordResult(latLong);
      return latLong;
    })
    .catch(() => {
      recordResult(null);
      return null;
    })
    .finally(() => {
      pendingRequest = null;
    });
  return pendingRequest;
}

export function clearGeoLatLongCache() {
  cache = null;
  consecutiveFailures = 0;
  sessionDisabled = false;
}
